import { useState } from "react";
import { useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import { Skeleton } from "@/components/ui/skeleton";
import { BadgeDetailSheet } from "./badge-detail-sheet";
import { cn } from "@/lib/utils";

type BadgeItem = {
  type: string;
  name: string;
  description: string;
  imageAsset: string;
  points: number;
  earned: boolean;
  awardedAt: number | null;
};

interface BadgeShowcaseProps {
  childId: string;
}

export function BadgeShowcase({ childId }: BadgeShowcaseProps) {
  const badges = useQuery(api.parent.badges.listForChild, {
    childId: childId as Id<"players">,
  });
  const [selected, setSelected] = useState<BadgeItem | null>(null);

  if (badges === undefined || badges === null) {
    return (
      <div className="grid grid-cols-2 gap-4 p-4">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-40 w-full rounded-2xl" />
        ))}
      </div>
    );
  }

  const earned = badges.filter((b) => b.earned).length;

  return (
    <>
      {/* Header count */}
      <div className="px-6 pt-6 pb-2">
        <h2 className="text-2xl font-bold text-gray-900">My Badges</h2>
        <p className="text-sm text-gray-500 mt-1">
          {earned} of {badges.length} earned
        </p>
      </div>

      {/* Badge grid */}
      <div className="grid grid-cols-2 gap-4 px-4 pb-4">
        {badges.map((badge) => (
          <button
            key={badge.type}
            onClick={() => setSelected(badge)}
            className={cn(
              "flex flex-col items-center gap-2 p-4 rounded-2xl border-2 text-center transition-all active:scale-95",
              badge.earned
                ? "bg-white border-amber-200 shadow-sm"
                : "bg-gray-50 border-gray-100 opacity-60"
            )}
          >
            <img
              src={`/badges/${badge.imageAsset}`}
              alt={badge.name}
              className={cn(
                "w-20 h-20 object-contain",
                !badge.earned && "grayscale"
              )}
            />
            <p
              className={cn(
                "text-sm font-semibold leading-tight",
                badge.earned ? "text-gray-800" : "text-gray-400"
              )}
            >
              {badge.name}
            </p>
            {badge.earned ? (
              <span className="text-xs text-amber-600 font-medium">
                ⭐ {badge.points} pts
              </span>
            ) : (
              <span className="text-xs text-gray-400 italic">Not yet earned</span>
            )}
          </button>
        ))}
      </div>

      <BadgeDetailSheet badge={selected} onClose={() => setSelected(null)} />
    </>
  );
}
